import { useState } from 'react';
import {
  adminGetThemes,
  adminCreateTheme,
  adminUpdateTheme,
  adminDeleteTheme,
} from '../../api/themesApi.js';
import {
  THEME_TYPES,
  THEME_FALLBACK_BG,
  THEME_AVAILABILITY,
  resolveThemeStatus,
  DEFAULT_AVAILABLE_NOTE,
  DEFAULT_ENDED_NOTE,
} from '../../data/themes.js';
import { useToast } from '../../context/ToastContext.jsx';
import { usePagedList } from '../../hooks/usePagedList.js';
import SortableTh from '../../components/ui/SortableTh.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import './AdminUploadPage.css';

function AdminThemesPage() {
  const { showToast } = useToast();
  const [mode, setMode] = useState('list');     // list | form
  const [editing, setEditing] = useState(null); // 編輯中的主題
  const [saving, setSaving] = useState(false);

  const openAddTheme = () => { setEditing(null); setMode('form'); };
  const openEditTheme = (t) => { setEditing(t); setMode('form'); };

  const onSaveTheme = async (payload) => {
    setSaving(true);
    try {
      if (editing) await adminUpdateTheme(editing.id, payload);
      else await adminCreateTheme(payload);
      showToast(editing ? '主題已更新' : '主題已新增', 'success', 2500);
      setMode('list'); // 回列表 → ThemeList 重新掛載 → 重新抓最新資料
      setEditing(null);
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error', 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>
          主題圖鑑管理
          <small>純展示的主題收錄 · 含絕版主題、取得來源</small>
        </h1>
      </div>

      {mode === 'list'
        ? <ThemeList onAdd={openAddTheme} onEdit={openEditTheme} />
        : <ThemeForm initial={editing} saving={saving} onSave={onSaveTheme} onCancel={() => { setMode('list'); setEditing(null); }} />
      }
    </>
  );
}

const TYPE_OPTS = [['all', '全部類型'], ...Object.entries(THEME_TYPES).map(([k, v]) => [k, v.label])];

// ── 主題圖鑑列表（後端分頁 + 排序 + 篩選,共用 usePagedList / SortableTh / Pagination）──
function ThemeList({ onAdd, onEdit }) {
  const { showToast } = useToast();
  const list = usePagedList(p => adminGetThemes(p), {
    limit: 10,
    initialSort: 'displayOrder,asc',
    initialFilters: { type: 'all', status: '' },
  });
  const { items, total, totalPages, page, setPage, sort, toggleSort, filters, setFilter, q, setQ, loading } = list;

  const del = async (t) => {
    if (!window.confirm(`確定刪除「${t.name}」？刪除後無法復原。`)) return;
    try {
      await adminDeleteTheme(t.id);
      showToast('主題已刪除', 'success', 2500);
      list.refetch();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error', 3000);
    }
  };

  const toggleActive = async (t) => {
    try {
      await adminUpdateTheme(t.id, { active: !t.active });
      showToast(t.active ? '主題已停用' : '主題已啟用', 'success', 2500);
      list.refetch();
    } catch (err) {
      showToast(`操作失敗：${err.message}`, 'error', 3000);
    }
  };

  return (
    <>
      <div className="admin-card" style={{ marginBottom: 16, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input className="au-search" placeholder="搜尋名稱 / 來源…" value={q} onChange={e => setQ(e.target.value)} />
        <select className="admin-select" style={{ maxWidth: 150 }} value={filters.type} onChange={e => setFilter('type', e.target.value)}>
          {TYPE_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="admin-select" style={{ maxWidth: 130 }} value={filters.status} onChange={e => setFilter('status', e.target.value)}>
          <option value="">全部狀態</option>
          <option value="active">顯示中</option>
          <option value="hidden">已下架</option>
        </select>
        <span style={{ marginLeft: 'auto', color: 'var(--ink-soft)', fontSize: 13 }}>共 {total} 筆</span>
        <button className="admin-btn primary" onClick={onAdd}>＋ 新增圖鑑主題</button>
      </div>
      <div className="admin-card admin-list-wrap" style={{ padding: 0, overflow: 'hidden' }}>
        {loading && items.length > 0 && (
          <div className="admin-list-busy"><span className="admin-spinner" />搜尋中…</div>
        )}
        <table className={`admin-table${loading && items.length > 0 ? ' is-busy' : ''}`}>
          <thead>
            <tr>
              <th style={{ width: 72 }}>縮圖</th>
              <SortableTh field="name" sort={sort} onSort={toggleSort}>名稱</SortableTh>
              <SortableTh field="type" sort={sort} onSort={toggleSort} style={{ width: 110 }}>類型</SortableTh>
              <SortableTh field="source" sort={sort} onSort={toggleSort} style={{ width: 150 }}>取得來源</SortableTh>
              <SortableTh field="acquiredDate" sort={sort} onSort={toggleSort} style={{ width: 120 }}>取得日期</SortableTh>
              <SortableTh field="active" sort={sort} onSort={toggleSort} style={{ width: 130 }}>狀態</SortableTh>
              <th style={{ width: 190 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0
              ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : items.length === 0
                ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--ink-soft)' }}>查無主題。</td></tr>
                : items.map(t => {
                  const img = t.images && t.images[0];
                  const st = resolveThemeStatus(t);
                  return (
                    <tr key={t.id}>
                      <td><div className="au-thumb" style={{ background: img ? `center/cover url(${img})` : (THEME_FALLBACK_BG[t.type] || '#222') }} /></td>
                      <td>
                        <div style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>{t.name}</div>
                        <div style={{ font: '500 11px Inter', color: 'var(--ink-soft)' }}>#{t.id}</div>
                      </td>
                      <td><span className="chip">{THEME_TYPES[t.type]?.label || t.type}</span></td>
                      <td style={{ font: '600 12px "Noto Sans TC"', color: 'var(--ink-soft)' }}>{t.source || '—'}</td>
                      <td style={{ font: '600 12px Inter', color: 'var(--ink-soft)' }}>{t.acquiredDate ? t.acquiredDate.replace(/-/g, '/') : '—'}</td>
                      <td>
                        <span className={`chip ${t.active ? 'active' : 'cancelled'}`}>{t.active ? '顯示中' : '已下架'}</span>
                        {st.state === 'ended' && <span className="chip cancelled" style={{ marginLeft: 4 }}>絕版</span>}
                        {st.state === 'available' && st.limited && <span className="chip active" style={{ marginLeft: 4 }}>限定中</span>}
                        {st.state === 'upcoming' && <span className="chip" style={{ marginLeft: 4 }}>即將開放</span>}
                      </td>
                      <td>
                        <button className="admin-btn ghost sm" onClick={() => onEdit(t)}>編輯</button>
                        <button className="admin-btn ghost sm" style={{ marginLeft: 6 }} onClick={() => toggleActive(t)}>{t.active ? '停用' : '啟用'}</button>
                        <button className="admin-btn ghost sm" style={{ marginLeft: 6, color: '#c62034' }} onClick={() => del(t)}>刪除</button>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

// ── 主題圖鑑表單（真 CRUD，依新 schema）──
function ThemeForm({ initial, nextOrder, saving, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [type, setType] = useState(initial?.type || 'd3');
  const [source, setSource] = useState(initial?.source || '');
  const [imagesText, setImagesText] = useState((initial?.images || []).join('\n'));
  const [videoUrl, setVideoUrl] = useState(initial?.videoUrl || '');
  const [story, setStory] = useState(initial?.story || '');
  const [displayOrder, setDisplayOrder] = useState(initial?.displayOrder ?? nextOrder ?? 0);
  const [active, setActive] = useState(initial?.active ?? true);
  const [availability, setAvailability] = useState(initial?.availability || (initial?.ended ? 'ended' : 'permanent'));
  const [availableFrom, setAvailableFrom] = useState(initial?.availableFrom || '');
  const [availableUntil, setAvailableUntil] = useState(initial?.availableUntil || '');
  const [availableNote, setAvailableNote] = useState(initial?.availableNote || '');
  const [endedNote, setEndedNote] = useState(initial?.endedNote || '');
  const [acquiredDate, setAcquiredDate] = useState(initial?.acquiredDate || '');

  const images = imagesText.split('\n').map(s => s.trim()).filter(Boolean);
  const firstImg = images[0];
  // 期間限定且起訖都填時，結束日不能早於開始日
  const dateRangeBad = availability === 'limited' && availableFrom && availableUntil && availableFrom > availableUntil;
  const canSave = name.trim().length > 0 && !dateRangeBad && !saving;

  const submit = () => {
    if (!canSave) return;
    onSave({
      name: name.trim(),
      type,
      source: source.trim(),
      images,
      videoUrl: videoUrl.trim(),
      story,
      displayOrder: Number(displayOrder) || 0,
      active,
      availability,
      // 只有期間限定才帶日期；其餘狀態清成 null，避免殘留
      availableFrom: availability === 'limited' ? (availableFrom || null) : null,
      availableUntil: availability === 'limited' ? (availableUntil || null) : null,
      availableNote: availableNote.trim(),
      endedNote: endedNote.trim(),
      acquiredDate: acquiredDate || null,
    });
  };

  return (
    <div className="admin-form admin-card">
      <p style={{ font: '600 13px "Noto Sans TC"', color: 'var(--ink-soft)', margin: '0 0 20px' }}>
        圖片 / 影片本期先<b>貼網址</b>（圖片一行一個，第一張為卡片縮圖），待之後接上傳功能改為直接上傳。
      </p>

      <div className="au-form-grid">
        {/* 媒體（貼網址） */}
        <div className="au-media-col">
          <div className="field">
            <label>圖片網址（一行一個）</label>
            <textarea
              value={imagesText}
              onChange={e => setImagesText(e.target.value)}
              placeholder={'https://example.com/1.jpg\nhttps://example.com/2.jpg'}
              rows={4}
            />
            <div className="au-preview" style={{ background: firstImg ? `center/cover url(${firstImg})` : (THEME_FALLBACK_BG[type] || '#222') }} />
          </div>
          <div className="field">
            <label>預覽影片網址（選填）</label>
            <input type="text" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="YouTube / Vimeo 連結，或 .mp4 直連網址" />
            <small style={{ color: 'var(--ink-soft)', fontSize: 12 }}>支援 YouTube、Vimeo、直連 mp4。卡片滑過靜音預覽、詳情頁可出聲播放。無影片則不顯示預覽。</small>
          </div>
        </div>

        {/* 欄位 */}
        <div>
          <div className="field">
            <label>主題名稱 *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="極光 AURORA" />
          </div>

          <div className="field">
            <label>主題類型 *</label>
            <div className="au-toggle-group">
              {Object.entries(THEME_TYPES).map(([k, v]) => (
                <button key={k} type="button" className={type === k ? 'on' : ''} onClick={() => setType(k)}>{v.label}</button>
              ))}
            </div>
          </div>

          <div className="row2">
            <div className="field">
              <label>取得來源</label>
              <input type="text" value={source} onChange={e => setSource(e.target.value)} placeholder="透過玩家獲得 / 透過活動獲得 / 以序號獲得" />
            </div>
            <div className="field">
              <label>取得日期</label>
              <input type="date" value={acquiredDate || ''} onChange={e => setAcquiredDate(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label>排序（數字小在前）</label>
            <input type="number" step="1" value={displayOrder} onChange={e => setDisplayOrder(e.target.value)} placeholder="0" />
          </div>

          <div className="field">
            <label>設計故事 / 參考資料</label>
            <textarea
              value={story}
              onChange={e => setStory(e.target.value)}
              placeholder="主題的設計背景、推出時間、取得方式…（支援多段落，換行會保留）"
              rows={5}
            />
          </div>

          <div className="field">
            <label>取得狀態 *</label>
            <div className="au-toggle-group">
              {Object.entries(THEME_AVAILABILITY).map(([k, v]) => (
                <button key={k} type="button" className={availability === k ? 'on' : ''} onClick={() => setAvailability(k)}>{v.label}</button>
              ))}
            </div>
            <small style={{ color: 'var(--ink-soft)', fontSize: 12 }}>
              「期間限定」會依下方起訖日期，過了結束日前台自動顯示「已絕版」。
            </small>
          </div>

          {availability === 'limited' && (
            <>
              <div className="row2">
                <div className="field">
                  <label>開始日</label>
                  <input type="date" value={availableFrom} onChange={e => setAvailableFrom(e.target.value)} />
                </div>
                <div className="field">
                  <label>結束日</label>
                  <input type="date" value={availableUntil} onChange={e => setAvailableUntil(e.target.value)} />
                </div>
              </div>
              {dateRangeBad && (
                <p style={{ color: '#c62034', font: '600 12px "Noto Sans TC"', margin: '-8px 0 12px' }}>
                  結束日不能早於開始日。
                </p>
              )}
            </>
          )}

          {availability !== 'ended' && (
            <div className="field">
              <label>可取得說明（留空用預設）</label>
              <textarea
                value={availableNote}
                onChange={e => setAvailableNote(e.target.value)}
                placeholder={DEFAULT_AVAILABLE_NOTE}
                rows={2}
              />
            </div>
          )}
          {availability !== 'permanent' && (
            <div className="field">
              <label>絕版說明（留空用預設）</label>
              <textarea
                value={endedNote}
                onChange={e => setEndedNote(e.target.value)}
                placeholder={DEFAULT_ENDED_NOTE}
                rows={2}
              />
            </div>
          )}

          <label className={`admin-check${active ? ' on' : ''}`} style={{ display: 'flex', maxWidth: 260 }}>
            <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
            顯示於圖鑑
          </label>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="admin-btn ghost" onClick={onCancel}>取消</button>
        <button type="button" className="admin-btn primary" onClick={submit} disabled={!canSave}>
          {saving ? '儲存中…' : '儲存'}
        </button>
      </div>
    </div>
  );
}

export default AdminThemesPage;
